/* eslint-disable no-console */
import CartItems from './cart-items';
import { onKeyUpEscape } from './trap-focus';
import CartNotification from './cart-notification';

export default class CartDrawer extends HTMLElement {
  cartInner: HTMLDivElement | null = null;
  activeElement: HTMLElement | null = null;
  productId?: string;
  dialog: HTMLDialogElement | null = null;

  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());

    this.dialog = document.getElementById('CartDrawer');
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }

    this.setHeaderCartIconListener();
    this.setNotifCartIconListener();

    this.cartInner = this.querySelector('#CartPanel');

    const closeBtns = this.querySelectorAll('[cart-drawer-close]');
    if (closeBtns.length > 0) {
      closeBtns.forEach((btn) => btn.addEventListener('click', this.close.bind(this)));
    }
  }

  setNotifCartIconListener() {
    const cartLink = document.querySelector('#cart-notification-button');

    if (!cartLink) return;

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open();
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open();
      }
    });
  }

  setHeaderCartIconListener() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open();
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open();
      }
    });
  }

  open() {
    window.scrollLock();

    //close all cart notifications
    const notifications = document.querySelectorAll('#cart-notification-dialog') as CartNotification[];

    if (notifications.length > 0) {
      notifications.forEach((notification) => {
        notification.close();
      });
    }

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    if (this.dialog) {
      this.dialog.showModal();
    }

    if (this.cartInner) {
      this.cartInner.classList.remove('translate-x-full');
      this.cartInner.classList.add('translate-x-0');
    }
  }

  close() {
    window.scrollUnlock();

    if (this.cartInner) {
      this.cartInner.classList.remove('translate-x-0');
      this.cartInner.classList.add('translate-x-full');
    }

    setTimeout(() => {
      if (this.dialog) {
        this.dialog.close();
      }
    }, 300);
  }

  setSummaryAccessibility(cartDrawerNote: HTMLElement) {
    if (!cartDrawerNote) return;

    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    const nextSibling = cartDrawerNote.nextElementSibling;
    if (nextSibling && nextSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', nextSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      const target = event.currentTarget as HTMLElement;
      if (!target) return;

      const detailsElement = target.closest('details');
      if (detailsElement) {
        const isOpen = detailsElement.hasAttribute('open');
        target.setAttribute('aria-expanded', (!isOpen).toString());
      }
    });

    const parentElement = cartDrawerNote.parentElement;
    if (parentElement) {
      parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState: { id?: string; sections?: Record<string, string> }) {
    if (!parsedState) return;

    const cartInner = this.querySelector('.cart__inner');
    if (cartInner && cartInner.classList.contains('is-empty')) {
      cartInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const sectionHTML = this.getSectionInnerHTML(parsedState.sections?.[section.id], section.selector);
      if (sectionHTML) {
        sectionElement.innerHTML = sectionHTML;
      }
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.open();
    });
  }

  getSectionInnerHTML(html: string, selector: string = '.shopify-section'): string | null {
    if (!html || !selector) return null;

    try {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const element = parsed.querySelector(selector);
      return element?.innerHTML || null;
    } catch (error) {
      console.warn('Error parsing section HTML:', error);
      return null;
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html: string, selector: string = '.shopify-section'): Element | null {
    if (!html || !selector) return null;

    try {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      return parsed.querySelector(selector);
    } catch (error) {
      console.warn('Error parsing section DOM:', error);
      return null;
    }
  }

  setActiveElement(element: HTMLElement) {
    if (element) {
      this.activeElement = element;
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'custom-main-cart-items',
        section: 'custom-main-cart-items',
        selector: '.js-contents',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}
